import styled from 'styled-components';

import Checkbox from 'components/shared/form/Checkbox';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { WorkflowLookupField, WorkflowLookupItem } from 'models/WorkflowLookup';
import { colors } from 'styles/theme/colors';

type Choice = { value: string; label: string };

type Props = {
  item: WorkflowLookupItem;
  fields: WorkflowLookupField[];
  // Entries of each parent list this item can point into, keyed by that list's key. Loaded for the
  // SAME scope as the item being edited, so a client's reason chooses from that client's types.
  parentOptions: Record<string, WorkflowLookupItem[]>;
  // False while a parent list is still loading or failed to load, so an empty picker is not
  // reported as an empty list.
  parentsResolved: boolean;
  disabled: boolean;
  onChange: (changes: Partial<WorkflowLookupItem>) => void;
};

// The extra fields a list's items carry, rendered from what the API says the list has rather than
// from anything hardcoded per list. A new field on a list reaches this screen without a change here.
export default function LookupItemFields({ item, fields, parentOptions, parentsResolved, disabled, onChange }: Props) {
  return (
    <>
      {fields.map((field) => {
        const value = item[field.name];

        if (field.type === 'boolean') {
          return (
            <Row key={field.name}>
              <Checkbox
                checked={Boolean(value)}
                disabled={disabled}
                label={field.label}
                size={16}
                labelSize='14px'
                onChange={(checked) => onChange({ [field.name]: checked })}
              />
            </Row>
          );
        }

        if (field.type === 'lookup' || field.type === 'enum') {
          // A lookup offers the parent list's live entries; an enum offers the fixed values the API
          // declared. Both are a choice, so both are a picker — typing either one is what let an
          // entry point at something that does not exist and then silently never appear.
          const choices: Choice[] =
            field.type === 'lookup'
              ? (parentOptions[field.parentKey ?? ''] ?? []).map((parent) => ({
                  value: parent.apiName,
                  label: parent.displayName,
                }))
              : field.options;

          const selected = choices.find((choice) => choice.value === String(value ?? '')) ?? null;

          return (
            <Row key={field.name}>
              <FieldLabel>
                {field.label}
                {field.required && <Required> *</Required>}
              </FieldLabel>
              <Select<Choice>
                options={choices}
                value={selected}
                getOptionValue={(choice) => choice.value}
                getOptionLabel={(choice) => choice.label}
                onChange={(choice) => onChange({ [field.name]: choice?.value ?? '' })}
                placeholder={`Select ${field.label.toLowerCase()}...`}
                isSearchable={false}
                disabled={disabled}
              />
              {field.type === 'lookup' && parentsResolved && choices.length === 0 && (
                <Hint>This client has no {field.label.toLowerCase()} entries yet. Add one to that list first.</Hint>
              )}
            </Row>
          );
        }

        return (
          <Row key={field.name}>
            <FieldLabel>
              {field.label}
              {field.required && <Required> *</Required>}
            </FieldLabel>
            <Input
              disabled={disabled}
              value={String(value ?? '')}
              onChange={(event) => onChange({ [field.name]: event.target.value })}
            />
          </Row>
        );
      })}
    </>
  );
}

const Row = styled.div`
  margin-bottom: 16px;
`;

const Required = styled.span`
  color: ${colors.red100};
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${colors.red100};
`;
